'use client'

import * as Sentry from '@sentry/nextjs'
import { Component, type ErrorInfo, type ReactNode } from 'react'

type MarkdownDescriptionErrorBoundaryProps = {
  /** Plain-text description shown when the Markdown description fails to render. */
  fallback: ReactNode
  children: ReactNode
}

type MarkdownDescriptionErrorBoundaryState = {
  hasError: boolean
}

/**
 * Keeps a Markdown description render failure inside the description (CONNECT-6178).
 *
 * @remarks
 * The server already renders the Markdown inside `try` and falls back to plain text, but the
 * resulting tree is still serialized and rendered later. A failure there must not reach the
 * route's `error.tsx` and take down the world page, so it is caught here and reported.
 */
class MarkdownDescriptionErrorBoundary extends Component<
  MarkdownDescriptionErrorBoundaryProps,
  MarkdownDescriptionErrorBoundaryState
> {
  state: MarkdownDescriptionErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): MarkdownDescriptionErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { feature: 'markdown-description' },
      extra: { componentStack: info.componentStack },
    })
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

export default MarkdownDescriptionErrorBoundary
